import { Flag } from "../components/Flag";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { PASSPORTS, CURRENCIES } from "../mock";
import { RATE, FX_META, setRates, SYM, setMoneyCurrency, toLocal, money, localSum, fmtLocal, moneyMul, moneySum, currentCurrency, moneyDual, localiseMoney, DEFAULT_CURRENCY } from "./money";

export { RATE, FX_META, setRates, SYM, setMoneyCurrency, toLocal, money, localSum, fmtLocal, moneyMul, moneySum, currentCurrency, moneyDual, localiseMoney };
export const ISO = { India: "IN", Nigeria: "NG", Pakistan: "PK", Philippines: "PH", Brazil: "BR", "United States": "US", "United Kingdom": "GB", China: "CN", Bangladesh: "BD", Vietnam: "VN", "Sri Lanka": "LK", Nepal: "NP", "United Arab Emirates": "AE" };
export const flagOf = (iso) => (/^[A-Za-z]{2}$/.test(iso || "") ? String.fromCodePoint(...[...iso.toUpperCase()].map((c) => 127397 + c.charCodeAt(0))) : "");

const KEY = "vl-locale";
export const FlagImg = ({ iso, size = 18, className = "" }) => (iso ? <Flag iso={String(iso)} size={size} className={className} /> : null);
const read = () => { try { return JSON.parse(localStorage.getItem(KEY)) || null; } catch { return null; } };
const Ctx = createContext(null);

// Country from the edge headers via the backend (CF-IPCountry and similar); no third-party geo call. Null means "no signal": India first, passport left for the visitor to choose.
async function detect() {
  try {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geo`, { signal: AbortSignal.timeout(3000) });
    if (!r.ok) throw new Error();
    const j = await r.json();
    if (!/^[A-Z]{2}$/.test(j.country || "")) return null;
    return { iso: j.country, name: j.name || Object.keys(ISO).find((k) => ISO[k] === j.country) || j.country };
  } catch { return null; }
}

// One currency story: every visitor sees INR until they pick another currency themselves in the header menu (currencyManual).
const settle = (st) => ({ ...st, currency: st.currencyManual && CURRENCIES.includes(st.currency) ? st.currency : DEFAULT_CURRENCY });

export function LocaleProvider({ children }) {
  const [state, setState] = useState(() => { const st = settle(read() || { passport: "India", iso: "IN", detected: false }); if (!/^[A-Z]{2}$/.test(st.iso || "")) st.iso = ISO[st.passport] || "IN"; setMoneyCurrency(st.currency); return st; });
  useEffect(() => { setMoneyCurrency(state.currency); }, [state.currency]);
  useEffect(() => {
    if (state.detected) return;
    let alive = true;
    detect().then((g) => {
      if (!alive) return;
      const next = g ? { ...state, passport: g.name, iso: g.iso, detected: true, detectedName: g.name, detectedIso: g.iso } : { ...state, passport: "India", iso: "IN", detected: true, unknown: true };
      localStorage.setItem(KEY, JSON.stringify(next)); setState(next);
    });
    return () => { alive = false; };
  }, [state.detected]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(() => {
    const save = (patch) => setState((p) => { const n = { ...p, ...patch, ...(patch.passport ? { unknown: false } : {}) }; localStorage.setItem(KEY, JSON.stringify(n)); return n; });
    const options = [...[...PASSPORTS].sort((a, b) => (a === "India" ? -1 : b === "India" ? 1 : 0)).map((n) => ({ name: n, iso: ISO[n] })), ...(state.detectedName && !PASSPORTS.includes(state.detectedName) ? [{ name: state.detectedName, iso: state.detectedIso }] : [])];
    return {
      ...state, options, flag: flagOf(state.iso),
      setPassport: (name) => { const iso = options.find((o) => o.name === name)?.iso || state.iso; save({ switches: (state.switches || 0) + 1, passport: name, iso }); },
      setCurrency: (currency) => { setMoneyCurrency(currency); save({ currency, currencyManual: true, switches: (state.switches || 0) + 1 }); },
      fmt: money,
    };
  }, [state]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useLocale = () => useContext(Ctx);
